// src/prototype/components/CFOPanel.tsx
// CFO Financial Summary — the money half of the Approval & Sign-off section.
// Leads with the four figures a CFO gate turns on, then the assumptions behind them,
// then anything flagged for validation, then the artifacts that carry it to a board.
import { useLocation } from 'react-router-dom';
import React from 'react';
import { DollarSign, FileText, BookOpen, Layers, TrendingUp, AlertTriangle } from 'lucide-react';
import type { CfoSummary } from '../data/executionInitiatives';
import { CANON_CONFIDENCE_BASIS } from '../roi/roiCanon';

/** Shortened for the tile; the full sentence lives in the Assumptions Appendix. */
const CONFIDENCE_NOTE = CANON_CONFIDENCE_BASIS;

/**
 * Approval artifacts, in the order a CFO would reach for them. Each opens a standalone
 * print page in a new tab — these routes render fixed board-ready documents and take no
 * parameters, which is the same contract EAActivatePage used.
 *
 * Their FINANCIAL figures now come from the ROI engine (src/prototype/roi), so they can no
 * longer disagree with this panel or the ROI Model page — that is what the reconciliation
 * tests in src/prototype/roi/__tests__/roiReconciliation.test.ts assert.
 *
 * TODO: the print pages are still fixed to the featured initiative (Care Coordination) and do
 * not switch on the initiative being viewed. src/prototype/artifacts/builders/* can generate
 * per-initiative content but is not wired to these routes; connecting the two is its own
 * piece of work.
 */
/**
 * Artifacts open in a NEW TAB, which has no router history and no router state — so the route
 * to come back to has to travel in the URL. The artifact shell reads ?returnTo and falls back
 * to /activate when it is absent (e.g. someone opens the print URL directly).
 */
export function appendReturnTo(route: string, from: string): string {
  const sep = route.includes('?') ? '&' : '?';
  return `${route}${sep}returnTo=${encodeURIComponent(from)}`;
}

const ARTIFACTS: { label: string; route: string; Icon: React.ElementType }[] = [
  { label: 'Export CFO Summary', route: '/use-case/cfo-summary/print', Icon: FileText },
  { label: 'Board Rationale Packet', route: '/use-case/board-rationale/print', Icon: BookOpen },
  { label: 'Executive Brief', route: '/use-case/executive-summary/print', Icon: FileText },
  { label: 'Assumptions Appendix', route: '/use-case/assumptions/print', Icon: Layers },
];

export interface CFOPanelProps {
  cfo: CfoSummary;
  /** Routes to the existing ROI Model view for this initiative. */
  onOpenRoiModel: () => void;
  /** The Execute hub shows artifacts in their own Artifact Export section. Default true. */
  showArtifacts?: boolean;
}

export const CFOPanel: React.FC<CFOPanelProps> = ({ cfo, onOpenRoiModel, showArtifacts = true }) => {
  const location = useLocation();
  // Where "Back to Execute" should land in the new tab: the surface that opened the artifact.
  const withReturnTo = (route: string) => appendReturnTo(route, location.pathname);

  const cells: { label: string; value: string; note?: string }[] = [
    { label: 'Annual benefit', value: cfo.benefit, note: cfo.benefitNote },
    { label: 'Investment', value: cfo.cost, note: cfo.costNote },
    { label: 'Payback', value: cfo.payback },
    // The confidence figure never travels without its basis. It used to be an
    // unexplained "75%" here and an unexplained "85%" in the CFO PDF.
    { label: 'Confidence', value: cfo.conf, note: cfo.conf === '—' ? undefined : CONFIDENCE_NOTE },
  ];

  return (
    <div className="rounded-xl border border-[#18365E]/20 bg-[#18365E]/[.03] p-4">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="w-4 h-4 text-[#18365E]" />
        <span className="text-xs font-semibold text-[#18365E] uppercase tracking-wide">CFO Financial Summary</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3">
        {cells.map(({ label, value, note }) => (
          <div key={label} className="bg-white rounded-lg border border-[#e5e7eb] px-3 py-2.5">
            <p className="text-[10px] text-[#717182] font-semibold uppercase tracking-wide">{label}</p>
            <p className="text-base font-bold text-[#18365E] leading-tight mt-0.5">{value}</p>
            {note && <p className="text-[10px] text-[#717182] mt-0.5">{note}</p>}
          </div>
        ))}
      </div>

      {cfo.assumptions.length > 0 && (
        <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-x-auto mb-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#F5F7FA] text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
                <th className="text-left px-3 py-2">Key assumption</th>
                <th className="text-left px-3 py-2">Value</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Confidence</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Source</th>
              </tr>
            </thead>
            <tbody>
              {cfo.assumptions.map((a) => (
                <tr key={a.label} className="border-t border-[#f1f5f9]">
                  <td className="px-3 py-2 text-[#4b5563]">{a.label}</td>
                  <td className="px-3 py-2 font-bold text-[#18365E]">{a.value}</td>
                  <td className="px-3 py-2 hidden sm:table-cell">
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        a.confidence === 'High' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}
                    >
                      {a.confidence}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#717182] hidden md:table-cell">{a.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cfo.flags.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-3">
          <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Flagged for validation
          </p>
          <ul className="space-y-1">
            {cfo.flags.map((flag) => (
              <li key={flag} className="text-xs text-amber-800 leading-relaxed flex gap-1.5">
                <span aria-hidden="true">·</span>
                {flag}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {showArtifacts && ARTIFACTS.map(({ label, route, Icon }) => (
          <button
            key={label}
            onClick={() => window.open(withReturnTo(route), '_blank')}
            className="text-xs font-semibold text-[#18365E] border border-[#18365E]/30 rounded-lg px-3 py-1.5 hover:bg-[#18365E] hover:text-white transition-colors flex items-center gap-1.5"
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
        {/* The ROI Model already exists in the MVP — this routes to it. */}
        <button
          onClick={onOpenRoiModel}
          className="text-xs font-bold text-white bg-[#FF6E4C] rounded-lg px-3 py-1.5 hover:bg-[#e85f3f] transition-colors flex items-center gap-1.5"
        >
          <TrendingUp className="w-3.5 h-3.5" />
          Open ROI Model
        </button>
      </div>
    </div>
  );
};

export default CFOPanel;
